<template>
  <div>
    <slot/>
  </div>
</template>

<script setup>
import Highcharts from "@/highcharts"
import { onMounted } from "vue"

const props = defineProps({
  categories: Array,
  borrowingAverage: Array,
  debtAverage: Array,
  privateDebt: Array,
  privateRange: Array,
  publicDebt: Array,
  publicRange: Array,
  year1: Number,
  year2: Number,
})

function formatter() {
  const eY1 = props.year1

  if (this.x == eY1) {
    return null
  }

  let index

  if (this.x == (eY1 + 1)) {
    index = this.series.data[0].y
    index = Math.round(((this.y - index) / index) * 1000) / 10
  }

  let y = props.year1
  let i = 1

  while (y <= props.year2) {
    if (this.x == (eY1 + 1 + i)) {
      index = this.series.data[i].y
      index = Math.round(((this.y - index) / index) * 1000) / 10
    }
    y++
    i++
  }

  let s_color

  if (index > 0) {
    s_color = "color:green;"
    index = `+${index}`
  } else if (index < 0) {
    s_color = "color:red;"
  } else {
    s_color = ""
  }

  return `<span style='font-size:13px;${s_color}'>${index}%</span>`
}

onMounted(() => {
  const { categories, borrowingAverage, debtAverage, privateRange, privateDebt, publicRange, publicDebt } = props

  new Highcharts.Chart({
    chart: {
      renderTo: "debt_average",
    },
    title: {
      text: "",
    },
    legend: {
      enabled: false,
    },
    subtitle: {
      text: "Average Amount Borrowed by Graduates Who Borrowed",
    },

    xAxis: {
      categories,
    },
    yAxis: [{
      gridLineWidth: 1,
      title: {
        text: "",
        style: {
          color: "#4572A7",
        },
      },
      labels: {
        formatter() {
          return `$${Math.round(this.value / 1000)}k`
        },
        style: {
          color: "#4572A7",
        },
      },
      opposite: false,
      endOnTick: true,
      min: 0,
    }],
    series: [
      {
        name: "Average Borrowed", type: "spline", color: "#3bc9db", yAxis: 0, data: debtAverage,
        dataLabels: {
          useHTML: true, enabled: true, style: { fontWeight: 500 }, x: 0, y: 5, borderRadius: 2,
          formatter,
        },
      },
    ],

  })

  new Highcharts.Chart({
    chart: {
      renderTo: "borrowing_average",
    },
    title: {
      text: "",
    },
    legend: {
      enabled: false,
    },
    subtitle: {
      text: "Percentage of Graduates Who Borrowed",
    },
    tooltip: {
      shared: false,
      crosshairs: false,
      formatter() {
        return `${Highcharts.numberFormat(this.y, 1, ".", ",")}%`
      },
    },
    xAxis: {
      categories,
    },
    yAxis: [{
      gridLineWidth: 1,
      title: {
        text: "",
        style: {
          color: "#4572A7",
        },
      },
      labels: {
        formatter() {
          return `${this.value}%`
        },
        style: {
          color: "#4572A7",
        },
      },
      opposite: false,
      endOnTick: false,
      min: 70,
      max: 100,
      tickInterval: 10,
    }],
    series: [{
      name: "Percentage Borrowing",
      type: "spline",
      color: "#3bc9db",
      yAxis: 0,
      data: borrowingAverage,
    }],
  })

  new Highcharts.Chart({
    chart: {
      renderTo: "private_debt_average",
    },
    title: {
      text: "Private Law School Graduates",
    },
    legend: {
      enabled: false,
    },
    subtitle: {
      text: "Average Amount Borrowed Among Graduates Who Borrowed",
    },
    tooltip: {
      shared: true,
      crosshairs: false,
      followPointer: true,
      followTouchMove: true,
    },
    xAxis: {
      categories,
    },
    yAxis: [{
      gridLineWidth: 1,
      title: {
        text: "",
        style: {
          color: "#4572A7",
        },
      },
      labels: {
        formatter() {
          return `$${Math.round(this.value / 1000)}k`
        },
        style: {
          color: "#4572A7",
        },
      },
      opposite: false,
      endOnTick: true,
      min: 0,
    }],
    series: [{
      name: "Average Borrowed",
      type: "spline",
      color: Highcharts.getOptions().colors[1],
      yAxis: 0,
      data: privateDebt,
      dataLabels: {
        useHTML: true,
        enabled: true,
        style: {
          fontWeight: 500,
        },
        x: 0,
        y: 5,
        borderRadius: 2,
        formatter,
      },
    }, {
      name: "Range",
      data: privateRange,
      type: "arearange",
      lineWidth: 0,
      linkedTo: ":previous",
      color: Highcharts.getOptions().colors[1],
      fillOpacity: 0.3,
      zIndex: 0,
      marker: {
        enabled: false,
      },
    }],
  })

  new Highcharts.Chart({
    chart: {
      renderTo: "public_debt_average",
    },
    title: {
      text: "Public Law School Graduates",
    },
    legend: {
      enabled: false,
    },
    subtitle: {
      text: "Average Amount Borrowed Among Graduates Who Borrowed",
    },
    tooltip: {
      shared: true,
      crosshairs: false,
      followPointer: true,
      followTouchMove: true,
    },
    xAxis: {
      categories,
    },
    yAxis: [{
      gridLineWidth: 1,
      title: {
        text: "",
        style: {
          color: "#4572A7",
        },
      },
      labels: {
        formatter() {
          return `$${Math.round(this.value / 1000)}k`
        },
        style: {
          color: "#4572A7",
        },
      },
      opposite: false,
      endOnTick: true,
      min: 0,
    }],
    series: [{
      name: "Average Borrowed",
      type: "spline",
      color: Highcharts.getOptions().colors[0],
      yAxis: 0,
      data: publicDebt,
      dataLabels: {
        useHTML: true,
        enabled: true,
        style: {
          fontWeight: 500,
        },
        x: 0,
        y: 5,
        borderRadius: 2,
        formatter,
      },
    }, {
      name: "Range",
      data: publicRange,
      type: "arearange",
      lineWidth: 0,
      linkedTo: ":previous",
      color: Highcharts.getOptions().colors[0],
      fillOpacity: 0.3,
      zIndex: 0,
      marker: {
        enabled: false,
      },
    }],
  })
})
</script>
